import React, { useState } from "react";
import "./UserAvatar.css";

// Phase H. Renders a user's profile photo when imgKey is set, or a
// colored circle with their initials when it's not. The circle's color
// is deterministic over the seed string (email preferred, then name)
// so the same person always shows up the same color across surfaces.
//
// Single source of truth for "what's the URL of a profile photo" —
// other consumers (CaptainShowcase, future leaderboard avatars,
// admin-tab UI) import this component instead of building the URL
// themselves.
//
// Props:
//   - name:    used to derive initials and (as a fallback) the seed color
//   - email:   preferred color seed; if absent we fall back to name
//   - imgKey:  S3 key like "profiles/<userId>/<ts>.jpg". When null/empty
//              the initials fallback renders.
//   - size:    px diameter — default 40
//   - title:   tooltip text (default to name)
//   - className: extra wrapper class for layout-specific tweaks

const S3_BASE = "https://s3.amazonaws.com/social-images-public/";

// Palette — readable on white backgrounds, distinct enough that two
// adjacent captains rarely collide. If you add a color, prefer a
// saturated background with white text (handled below).
const PALETTE = [
  "#2f6feb",
  "#d6336c",
  "#0ca678",
  "#7048e8",
  "#f59f00",
  "#1098ad",
  "#e8590c",
  "#5c940d",
  "#9c36b5",
  "#0b7285",
];

const pickColor = (seed) => {
  const s = (seed || "").toLowerCase();
  if (!s) return PALETTE[0];
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  }
  return PALETTE[hash % PALETTE.length];
};

const initialsOf = (name) => {
  if (typeof name !== "string") return "?";
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (
    parts[0].charAt(0).toUpperCase() +
    parts[parts.length - 1].charAt(0).toUpperCase()
  );
};

const UserAvatar = ({
  name = "",
  email = "",
  imgKey = null,
  size = 40,
  title,
  className = "",
}) => {
  const [imgFailed, setImgFailed] = useState(false);
  const showImg = !!imgKey && !imgFailed;
  const initials = initialsOf(name) || initialsOf(email);
  const seed = email || name;
  const bg = pickColor(seed);
  const style = {
    width: size,
    height: size,
    fontSize: Math.max(11, Math.round(size * 0.4)),
    lineHeight: `${size}px`,
  };

  const wrapperClass = `user-avatar ${className}`.trim();
  const tooltip = title || name || email || "";

  if (showImg) {
    return (
      <span className={wrapperClass} style={style} title={tooltip}>
        <img
          src={`${S3_BASE}${imgKey}`}
          alt={tooltip || "Profile photo"}
          onError={() => setImgFailed(true)}
          loading="lazy"
          style={{ width: size, height: size }}
        />
      </span>
    );
  }

  return (
    <span
      className={`${wrapperClass} user-avatar--initials`}
      style={{ ...style, backgroundColor: bg }}
      title={tooltip}
      aria-label={tooltip || "User"}
    >
      {initials}
    </span>
  );
};

export default UserAvatar;
